// Centralized project-scoped path builder. See
// docs/product/bugs/connect-cta-new-tab-project-leak/trd.md Section 3,
// R5. Every internal navigate()/Link-to/URL-building call site that targets
// one of the three project-scoped routes (/:projectId/grant-access,
// /:projectId/analyze/*, /:projectId/settings) uses this helper
// instead of an ad hoc template literal, so the prefixing convention isn't
// duplicated inconsistently across the ~15 call sites that need it (TRD
// Section 3, R6).
export function buildProjectPath(projectId, path) {
  return `/${projectId}${path.startsWith('/') ? path : `/${path}`}`;
}

// The three project-scoped route shapes under /:projectId in App.js's
// route tree (docs/product/bugs/open/new-tab-link-wrong-project-unscoped-cta-url/trd.md,
// Bounded Unit 2, R2.1).
const KNOWN_SUFFIX_RE = /^\/(grant-access|analyze|settings|visibility-planner)(\/|$)/;

/**
 * Safety-net normalizer for server-supplied cta_url values that are
 * supposed to resolve to a project-scoped route but may be missing the
 * current project's id prefix, carry a stale/wrong project id, or (pre
 * cq-api fix) use the old non-project-scoped `/project/:id/...` shape.
 * Exact algorithm per the TRD (Bounded Unit 2, R2.1) -- no discretion.
 *
 * @param {*} rawUrl - the raw cta_url value from a message row.
 * @param {string|null|undefined} currentProjectId
 * @returns {*} the normalized (or unchanged) url.
 */
export function normalizeProjectScopedCtaUrl(rawUrl, currentProjectId) {
  // Step 1: open-redirect-guard-style check (mirrors postAuthRoute.js's
  // isValidRedirectUrl) -- not a string, empty, doesn't start with '/',
  // or starts with '//' or '/\' -> leave untouched. Also covers the
  // falsy-currentProjectId guard (R2.3): if there's no current project to
  // scope against, never construct a path off it.
  if (
    typeof rawUrl !== 'string' ||
    rawUrl.length === 0 ||
    !rawUrl.startsWith('/') ||
    rawUrl.startsWith('//') ||
    rawUrl.startsWith('/\\') ||
    !currentProjectId
  ) {
    return rawUrl;
  }

  // Step 3: bare, unprefixed known route (e.g. '/grant-access').
  if (KNOWN_SUFFIX_RE.test(rawUrl)) {
    return buildProjectPath(currentProjectId, rawUrl);
  }

  // Step 4: already correctly scoped to the current project.
  const currentProjectPrefix = `/${currentProjectId}`;
  if (rawUrl.startsWith(currentProjectPrefix)) {
    const remainderForCurrent = rawUrl.slice(currentProjectPrefix.length);
    if (KNOWN_SUFFIX_RE.test(remainderForCurrent)) {
      return rawUrl;
    }
  }

  // Step 5: strip the first path segment (stale/wrong project id, or the
  // literal 'project' segment from the pre-fix '/project/:id/billing'
  // shape) and test the remainder.
  const remainder = rawUrl.replace(/^\/[^/]+/, '');
  if (KNOWN_SUFFIX_RE.test(remainder)) {
    return buildProjectPath(currentProjectId, remainder);
  }

  // Step 6: unrecognized internal shape -- leave untouched, don't
  // guess-rewrite.
  return rawUrl;
}
